import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "../../../context/AuthContext";
import { clearProfileCache } from "../../../hooks/useProfile";
import { sessionManager } from "../../../utils/sessionManager";
import { setClientIconUrl } from "../../../utils/clientIcon";
import { prefetchProfilePicture } from "../../(All) Sidebar/hooks/useProfilePicture";

// ─── Splash URL Helpers ────────────────────────────────────────────────────────

const resolveSplashUrl = (record, baseUrl) => {
  if (!record) return null;
  const source = record.fullUrl || record.gifUrl;
  if (!source) return null;
  const trimmed = source.trim();
  if (!trimmed) return null;
  if (
    trimmed.toLowerCase() === "null" ||
    trimmed.toLowerCase() === "undefined"
  ) {
    return null;
  }
  if (/^https?:\/\//i.test(source)) return source;
  if (!baseUrl) return source.startsWith("/") ? source : `/${source}`;
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = source.startsWith("/") ? source : `/${source}`;
  return `${normalizedBase}${normalizedPath}`;
};

const buildSplashUrlCandidates = (resolvedUrl, record, baseUrl) => {
  const candidates = [];
  const pushUnique = (value) => {
    if (!value) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (
      trimmed.toLowerCase() === "null" ||
      trimmed.toLowerCase() === "undefined"
    )
      return;
    if (!candidates.includes(trimmed)) candidates.push(trimmed);
  };

  pushUnique(resolvedUrl);

  if (record?.gifUrl) {
    const relativePath = record.gifUrl.startsWith("/")
      ? record.gifUrl
      : `/${record.gifUrl}`;
    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      pushUnique(`${normalizedBase}${relativePath}`);
    }
    pushUnique(
      `${window.location.origin.replace(/\/$/, "")}${relativePath}`
    );
  }

  if (resolvedUrl) {
    try {
      const parsed = new URL(resolvedUrl, window.location.origin);
      const pageProtocol = window.location.protocol;
      if (parsed.protocol === "https:" && pageProtocol === "http:") {
        const httpVariant = new URL(parsed.toString());
        httpVariant.protocol = "http:";
        pushUnique(httpVariant.toString());

        const httpAlt = new URL(httpVariant.toString());
        if (!httpAlt.port && parsed.port) {
          httpAlt.port = parsed.port;
          pushUnique(httpAlt.toString());
        }

        if (httpAlt.port === "3000") {
          const httpPortVariant = new URL(httpAlt.toString());
          httpPortVariant.port = "3002";
          pushUnique(httpPortVariant.toString());
        }
      }
    } catch (error) {
      console.debug("Unable to build HTTP fallback for splash URL", error);
    }
  }

  return candidates;
};

// ─── Hook ──────────────────────────────────────────────────────────────────────

const useLoginForm = (t) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [splashOverlay, setSplashOverlay] = useState({
    visible: false,
    url: null,
    loading: false,
  });

  // Reset password modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState(null);
  const [resetMessage, setResetMessage] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);

  // Forced password change — a phone-only account (no email on file) can't
  // be sent a reset link, so instead of blocking login entirely (like the
  // email first-login flow does), the server lets it log in once and flags
  // `mustChangePassword: true`. This modal blocks navigation into the app
  // until a new password is actually set.
  const [isForcePasswordChangeOpen, setIsForcePasswordChangeOpen] = useState(false);
  const [forcePasswordChangeError, setForcePasswordChangeError] = useState("");
  const [isForcePasswordChangeLoading, setIsForcePasswordChangeLoading] = useState(false);
  // Holds the login response so the rest of the post-login flow (profile
  // fetch, splash, navigation) can resume once the password change succeeds.
  const pendingLoginDataRef = useRef(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const splashTimerRef = useRef(null);
  const splashFailsafeTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
      if (splashFailsafeTimerRef.current)
        clearTimeout(splashFailsafeTimerRef.current);
    };
  }, []);

  // ✅ If this tab is already authenticated when the login page mounts —
  // either because it was opened straight to /login while logged in, or
  // because another tab just logged in and sessionManager's login broadcast
  // reloaded this one — send it straight to the right dashboard instead of
  // leaving it sitting on the login form. Without this, logging in on tab A
  // never "reached" tab B: the shared token/role were already in
  // localStorage, but nothing told an already-open login tab to look.
  useEffect(() => {
    if (sessionManager.isAuthenticated()) {
      const role = sessionManager.getUserRole();
      const target = getNavigationTarget(role);
      if (target) {
        navigate(target, { replace: true });
      }
    }
    // Only ever check once, on mount — handleSubmit owns the post-login
    // navigation for an actual login performed in this tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Navigation Target Helper ──────────────────────────────────────────────

  const getNavigationTarget = (role) => {
    switch (role) {
      case "admin":
        localStorage.setItem("viewType", "user");
        return "/userprofileadmin/professionalProfile";
      case "user":
        localStorage.setItem("viewType", "user");
        return "/userprofile";
      case "manager":
        localStorage.setItem("viewType", "user");
        return "/userprofilemanager/professionalProfile";
      case "superadmin":
        localStorage.setItem("viewType", "superadmin");
        return "/DashboardSuper/ConfigurationManagement";
      default:
        return null;
    }
  };

  // ─── Splash Logic ──────────────────────────────────────────────────────────

  const handleSplashNavigation = (activeSplash, clientName, target, BASE_URL) => {
    const splashUrl = resolveSplashUrl(activeSplash, BASE_URL);

    if (splashUrl) {
      const candidateUrls = buildSplashUrlCandidates(
        splashUrl,
        activeSplash,
        BASE_URL
      );

      if (candidateUrls.length > 0) {
        const splashImage = new window.Image();
        let attemptIndex = 0;

        const finalizeNavigation = (delay) => {
          if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
          splashTimerRef.current = setTimeout(() => {
            setSplashOverlay({ visible: false, url: null, loading: false });
            navigate(target);
            splashTimerRef.current = null;
          }, delay);
        };

        const cleanupFailsafe = () => {
          if (splashFailsafeTimerRef.current) {
            clearTimeout(splashFailsafeTimerRef.current);
            splashFailsafeTimerRef.current = null;
          }
        };

        const startFailsafe = () => {
          if (!splashFailsafeTimerRef.current) {
            splashFailsafeTimerRef.current = setTimeout(() => {
              setSplashOverlay((prev) =>
                prev.visible ? { ...prev, loading: false } : prev
              );
              navigate(target);
              splashFailsafeTimerRef.current = null;
            }, 7000);
          }
        };

        const tryLoad = (index) => {
          const nextUrl = candidateUrls[index];
          if (!nextUrl) {
            cleanupFailsafe();
            finalizeNavigation(500);
            return;
          }
          setSplashOverlay({ visible: true, url: nextUrl, loading: true });
          splashImage.src = nextUrl;
        };

        splashImage.onload = () => {
          cleanupFailsafe();
          setSplashOverlay({
            visible: true,
            url: candidateUrls[attemptIndex],
            loading: false,
          });
          finalizeNavigation(6000);
        };

        splashImage.onerror = () => {
          attemptIndex += 1;
          if (attemptIndex < candidateUrls.length) {
            tryLoad(attemptIndex);
            return;
          }
          cleanupFailsafe();
          setSplashOverlay({ visible: true, url: null, loading: true });
          finalizeNavigation(800);
        };

        startFailsafe();
        tryLoad(attemptIndex);
        return;
      }
    }

    // No splash — navigate directly
    setSplashOverlay({ visible: false, url: null, loading: false });
    splashTimerRef.current = setTimeout(() => {
      navigate(target);
      splashTimerRef.current = null;
    }, 500);
    if (splashFailsafeTimerRef.current) {
      clearTimeout(splashFailsafeTimerRef.current);
      splashFailsafeTimerRef.current = null;
    }
  };

  // ─── Post-authentication continuation ──────────────────────────────────────
  // Runs the rest of a successful login (profile fetch, splash, navigation)
  // — called either immediately after /users/login, or after a forced
  // password change completes for a phone-only first login.
  const completeLoginFlow = async (data) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    // No-op when this runs after the forced password-change modal — the
    // token was already stashed to localStorage the moment mustChangePassword
    // came back, so the authenticated change-password call could use it.
    localStorage.setItem("token", data.token);
    clearProfileCache();

    // Fetch profile
    let profile = null;
    let role = null;
    let clientName = null;

    try {
      const profileRes = await apiFetch(`${BASE_URL}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token}`,
        },
      });

      if (profileRes.ok) {
        const profileJson = await profileRes.json();
        if (profileJson.success && profileJson.profile) {
          profile = profileJson.profile;
          Object.entries(profile).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              localStorage.setItem(
                key,
                typeof value === "object"
                  ? JSON.stringify(value)
                  : value
              );
            }
          });
          localStorage.setItem("email", email);
          if (profile.role) role = profile.role;
          if (profile.clientName) clientName = profile.clientName;

          // Warm the profile picture cache so the topbar avatar is
          // ready the moment the dashboard renders (fire-and-forget)
          if (profile.infinityCode) {
            prefetchProfilePicture(profile.infinityCode);
          }
        }
      }
    } catch (profileErr) {
      console.warn("Failed to fetch user profile after login", profileErr);
    }

    const target = getNavigationTarget(role);
    if (!target) {
      setError(t.noPermission);
      setIsLoading(false);
      return;
    }

    setSuccess(t.loginSuccessful);
    window.dispatchEvent(new Event("managerViewTypeChanged"));

    // Fetch splash if not in login response
    let splashRecord = data?.splashScreen || null;
    if ((!splashRecord || !splashRecord.gifUrl) && clientName) {
      try {
        const splashRes = await apiFetch(
          `${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`
        );
        if (splashRes.ok) {
          const splashJson = await splashRes.json();
          if (splashJson?.success && splashJson?.data) {
            splashRecord = splashJson.data;
          }
        }
      } catch (splashError) {
        console.warn("Failed to pull splash screen after login", splashError);
      }
    }

    const activeSplash =
      splashRecord && splashRecord.isActive !== false
        ? splashRecord
        : null;

    const clientIconUrl =
      activeSplash?.iconFullUrl || activeSplash?.iconUrl || null;
    setClientIconUrl(clientIconUrl);

    login(email, rememberMe, {
      email: profile?.employeeEmail || email,
      role,
      workingCompany: profile?.workingCompany,
      clientIconUrl,
      token: data.token,
      adminLevel: profile?.adminLevel || null,
    });

    handleSplashNavigation(activeSplash, clientName, target, BASE_URL);
  };

  // ─── Forced Password Change Handlers ───────────────────────────────────────

  const handleForcePasswordChangeSubmit = async (newPassword) => {
    setIsForcePasswordChangeLoading(true);
    setForcePasswordChangeError("");
    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const data = pendingLoginDataRef.current;
      const token = data?.token || localStorage.getItem("token");

      const res = await apiFetch(`${BASE_URL}/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // `password` is the one they just used to log in (the provisioned
        // default) — no need to make them retype it as "old password".
        body: JSON.stringify({ oldPassword: password, newPassword }),
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        throw new Error(result.error || t.networkError);
      }

      setIsForcePasswordChangeOpen(false);
      setIsLoading(true);
      await completeLoginFlow(data);
    } catch (err) {
      setForcePasswordChangeError(err.message || t.networkError);
    } finally {
      setIsForcePasswordChangeLoading(false);
    }
  };

  // Bails out of the forced password change instead of completing it — drops
  // the token that was stashed for the change-password call so the tab goes
  // back to a clean, logged-out login form rather than a half-authenticated
  // state.
  const handleForcePasswordChangeLogoutInstead = () => {
    localStorage.removeItem("token");
    pendingLoginDataRef.current = null;
    setIsForcePasswordChangeOpen(false);
    setForcePasswordChangeError("");
    setPassword("");
  };

  // ─── Submit Handler ────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSplashOverlay({ visible: false, url: null, loading: false });

    if (splashTimerRef.current) {
      clearTimeout(splashTimerRef.current);
      splashTimerRef.current = null;
    }
    if (splashFailsafeTimerRef.current) {
      clearTimeout(splashFailsafeTimerRef.current);
      splashFailsafeTimerRef.current = null;
    }

    // `email` is really "email or phone number" now — only lowercase it when
    // it actually looks like an email (lowercasing a phone number is a
    // no-op for digits anyway, but avoid mangling a leading "+").
    const trimmedIdentifier = email.trim();
    const normalizedIdentifier = trimmedIdentifier.includes("@")
      ? trimmedIdentifier.toLowerCase()
      : trimmedIdentifier;

    if (!normalizedIdentifier || !password) {
      setError(t.fillAllFields);
      return;
    }

    // NOTE: this used to short-circuit here by calling
    // sessionManager.getActiveSession() and, if ANY session was active in
    // ANY tab (any user), logging this tab in as that session — completely
    // skipping password verification and ignoring the email/password just
    // typed. That's what produced "weird" data when two tabs were open at
    // once: submitting the login form in tab 2 could silently log it in as
    // whoever's session was active in tab 1. Real authentication must
    // always go through the API below.

    setIsLoading(true);

    // Location is best-effort telemetry only — it must NEVER block login.
    // (The old flow wrapped the whole login call in getCurrentPosition, so a
    // denied/blocked/timed-out location prompt failed every sign-in before it
    // even reached the server.)
    const performLogin = async (position = null) => {
        const BASE_URL = process.env.REACT_APP_BASE_URL;

        try {
          const res = await apiFetch(`${BASE_URL}/users/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              identifier: normalizedIdentifier,
              password,
              ...(position?.coords
                ? {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                  }
                : {}),
            }),
          });

          const data = await res.json();

          if (res.ok) {
            if (data.firstLogin) {
              setError(data.message || t.firstLoginDetected);
              setIsLoading(false);
              return;
            }

            // Phone-only account, first login: server let it through but
            // flags that the (shared/default) password must be changed
            // before the user gets into the app. Stash the token now (the
            // change-password call needs it) and pause here — the rest of
            // this success path (profile fetch, splash, navigation) resumes
            // in completeLoginFlow() once the modal reports success.
            if (data.mustChangePassword) {
              localStorage.setItem("token", data.token);
              pendingLoginDataRef.current = data;
              setForcePasswordChangeError("");
              setIsForcePasswordChangeOpen(true);
              setIsLoading(false);
              return;
            }

            await completeLoginFlow(data);
          } else {
            if (data.subscriptionError) {
              switch (data.errorType) {
                case "not_found":
                  setError(t.subscriptionNotFound);
                  break;
                case "inactive":
                  setError(t.subscriptionInactive);
                  break;
                case "expired":
                  setError(data.error || t.subscriptionExpired);
                  break;
                default:
                  setError(data.error || t.subscriptionValidationFailed);
              }
            } else if (res.status === 401) {
              // Wrong email/password — show the translated message, not the
              // server's raw "Authentication failed"
              setError(t.invalidCredentials);
            } else {
              setError(data.error || t.invalidCredentials);
            }
          }
        } catch (err) {
          console.error("Login error:", err);
          setError(t.networkError);
        } finally {
          setIsLoading(false);
        }
    };

    // Fire login immediately; attach coordinates only if the browser answers
    // fast. Never wait on (or fail because of) the location prompt.
    if (navigator.geolocation) {
      let started = false;
      const startOnce = (position) => {
        if (!started) { started = true; performLogin(position); }
      };
      const timer = setTimeout(() => startOnce(null), 1500);
      navigator.geolocation.getCurrentPosition(
        (position) => { clearTimeout(timer); startOnce(position); },
        () => { clearTimeout(timer); startOnce(null); },
        { timeout: 1200, maximumAge: 600000 }
      );
    } else {
      performLogin(null);
    }
  };

  // ─── Reset Password Handlers ───────────────────────────────────────────────

  const handleOpenResetModal = (e) => {
    e.preventDefault();
    setIsResetModalOpen(true);
  };

  const handleCloseResetModal = () => {
    setResetEmail("");
    setResetStatus(null);
    setResetMessage("");
    setIsResetLoading(false);
    setIsResetModalOpen(false);
  };

  const handleResetPasswordSubmit = async () => {
    const normalizedResetEmail = resetEmail.toLowerCase().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(normalizedResetEmail)) {
      setResetStatus("error");
      setResetMessage(t.validEmailRequired);
      return;
    }

    setIsResetLoading(true);
    setResetStatus(null);
    setResetMessage("");

    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await apiFetch(`${BASE_URL}/users/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedResetEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResetStatus("success");
        setResetMessage(t.resetInstructionsSent);

        if (process.env.NODE_ENV === "development" && data.resetToken) {
          console.log("🔑 Development Reset Token:", data.resetToken);
        }

        setTimeout(handleCloseResetModal, 3000);
      } else {
        setResetStatus("error");
        setResetMessage(data.error || t.resetInstructionsFailed);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setResetStatus("error");
      setResetMessage(t.networkErrorReset);
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleResetKeyPress = (e) => {
    if (e.key === "Enter" && !isResetLoading && resetStatus !== "success") {
      handleResetPasswordSubmit();
    }
  };

  return {
    // Form state
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    error,
    success,
    isLoading,
    splashOverlay,
    // Handlers
    handleSubmit,
    // Reset modal state
    isResetModalOpen,
    resetEmail, setResetEmail,
    resetStatus,
    resetMessage,
    isResetLoading,
    // Reset modal handlers
    handleOpenResetModal,
    handleCloseResetModal,
    handleResetPasswordSubmit,
    handleResetKeyPress,
    // Forced password change modal (phone-only first login)
    isForcePasswordChangeOpen,
    forcePasswordChangeError,
    isForcePasswordChangeLoading,
    handleForcePasswordChangeSubmit,
    handleForcePasswordChangeLogoutInstead,
  };
};

export default useLoginForm;